import React from "react";
import { UseFormReturn } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Euro, Award, Target, AlertCircle, CheckCircle } from "lucide-react";
import { ExpertApplicationData, packageOptions, availableSpecializations, certificationOptions } from "./schema";
import { PricingGuidance } from "./PricingGuidance";

interface PricingStepProps {
  form: UseFormReturn<ExpertApplicationData>;
  onPrev: () => void;
  selectedPackages: string[];
  onPackageChange: (packageId: string, checked: boolean) => void;
  selectedSpecializations: string[];
  selectedCertifications: string[];
  isSubmitting: boolean;
  isSending: boolean;
  isUploading: boolean;
  onCheckStatus?: () => void;
}

export function PricingStep({ 
  form, 
  onPrev, 
  selectedPackages, 
  onPackageChange,
  selectedSpecializations,
  selectedCertifications,
  isSubmitting,
  isSending,
  isUploading,
  onCheckStatus
}: PricingStepProps) {
  const { register, formState: { errors }, setValue, watch, getValues } = form;
  const packagePricing = watch("packagePricing") || {};

  const handlePackagePriceChange = (packageId: string, price: number) => {
    const updatedPricing = { ...packagePricing, [packageId]: price };
    setValue("packagePricing", updatedPricing);
  };

  // Get specialization and certification labels
  const getSpecializationLabel = (id: string) => {
    return availableSpecializations.find(s => s.id === id)?.label || id;
  };

  const getCertificationLabel = (id: string) => {
    return certificationOptions.find(c => c.id === id)?.label || id;
  };

  // Check if expert can offer a package based on specializations
  const canOfferPackage = (packageId: string) => {
    const packageOption = packageOptions.find(p => p.id === packageId);
    if (!packageOption) return true;
    
    if (packageOption.requiredSpecializations.length === 0) return true;
    
    return packageOption.requiredSpecializations.some(spec => 
      selectedSpecializations.includes(spec)
    );
  };

  // Function to get validation status for this step
  const getValidationStatus = () => {
    const formData = getValues();
    const missingFields: string[] = [];
    const completedFields: string[] = [];

    // Check package selection
    if (!formData.offeredPackages || formData.offeredPackages.length === 0) {
      missingFields.push("At least one Service Package");
    } else {
      completedFields.push(`${formData.offeredPackages.length} Service Package(s) Selected`);
    }

    // Check package pricing
    const packagePricing = formData.packagePricing || {};
    const selectedPackages = formData.offeredPackages || [];
    const missingPricing: string[] = [];
    const completedPricing: string[] = [];

    selectedPackages.forEach(packageId => {
      const packageName = packageId === 'introductory' ? 'Introductory Consultation' : 
        packageId === 'standard' ? 'Standard Support Package' : 'Premium Case Management';
      
      if (!packagePricing[packageId] || packagePricing[packageId] < 10) {
        missingPricing.push(packageName);
      } else {
        completedPricing.push(`${packageName} (€${packagePricing[packageId]})`);
      }
    });

    if (missingPricing.length > 0) {
      missingFields.push(`Pricing for: ${missingPricing.join(', ')}`);
    }
    if (completedPricing.length > 0) {
      completedFields.push(`Pricing Set: ${completedPricing.join(', ')}`);
    }

    // Check terms agreement
    if (!formData.agreeToTerms) {
      missingFields.push("Terms Agreement");
    } else {
      completedFields.push("Terms Agreement");
    }

    return { missingFields, completedFields };
  };

  const { missingFields, completedFields } = getValidationStatus();
  const isStepComplete = missingFields.length === 0;

  return (
    <div className="space-y-6">
      {/* Validation Summary */}
      {!isStepComplete && (
        <Alert className="border-orange-200 bg-orange-50">
          <AlertCircle className="h-4 w-4 text-orange-600" />
          <AlertDescription className="text-orange-800">
            <div className="font-medium mb-2">Complete these items to submit your application:</div>
            <ul className="list-disc list-inside space-y-1 text-sm">
              {missingFields.map((field, index) => (
                <li key={index}>{field}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {isStepComplete && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            <div className="font-medium mb-2">✅ All required fields completed!</div>
            <ul className="list-disc list-inside space-y-1 text-sm">
              {completedFields.map((field, index) => (
                <li key={index}>{field}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Expert Summary */}
      <Card className="border-green-200 bg-green-50">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-green-800">
            <Target className="w-5 h-5" />
            Your Expertise Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Specializations */}
          {selectedSpecializations.length > 0 && (
            <div>
              <h4 className="font-medium text-green-800 mb-2">Areas of Specialization:</h4>
              <div className="flex flex-wrap gap-2">
                {selectedSpecializations.map(spec => (
                  <span key={spec} className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                    {getSpecializationLabel(spec)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Certifications */}
          {selectedCertifications.length > 0 && (
            <div>
              <h4 className="font-medium text-green-800 mb-2">Professional Certifications:</h4>
              <div className="flex flex-wrap gap-2">
                {selectedCertifications.map(cert => (
                  <span key={cert} className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm flex items-center gap-1">
                    <Award className="w-3 h-3" />
                    {getCertificationLabel(cert)}
                  </span>
                ))}
              </div>
            </div>
          )}

          <p className="text-sm text-green-700 italic">
            💡 Your specializations and certifications help families understand your expertise and may influence which service packages you can offer.
          </p>
        </CardContent>
      </Card>

      {/* Pricing Guidance */}
      <Card className="border-blue-200 bg-blue-50">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-blue-800">
            <Euro className="w-5 h-5" />
            Pricing & Service Packages
          </CardTitle>
        </CardHeader>
        <CardContent>
          <PricingGuidance />
        </CardContent>
      </Card>

      {/* Package Selection with Pricing */}
      <div>
        <Label className="text-base font-medium">Select Service Packages & Set Your Pricing *</Label>
        <p className="text-sm text-gray-600 mb-4">Choose which packages you'd like to offer and set your own prices for each.</p>
        
        <div className="space-y-4">
          {packageOptions.map((pkg) => {
            const canOffer = canOfferPackage(pkg.id);
            const isSelected = selectedPackages.includes(pkg.id);
            const currentPrice = packagePricing[pkg.id] || '';

            return (
              <div key={pkg.id} className={`border rounded-lg p-4 ${canOffer ? 'hover:bg-gray-50' : 'opacity-60 bg-gray-50'}`}>
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id={pkg.id}
                    checked={isSelected}
                    disabled={!canOffer}
                    onCheckedChange={(checked) => 
                      onPackageChange(pkg.id, checked as boolean)
                    }
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900">{pkg.name}</h4>
                      <span className="text-sm text-gray-600">Suggested: {pkg.suggestedPrice}</span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{pkg.duration}</p>
                    <ul className="text-sm text-gray-600 space-y-1 mb-3">
                      {pkg.includes.map((item, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-grandpal-green mr-2">•</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-blue-700 italic mb-3">Ideal for: {pkg.idealFor}</p>
                    
                    {/* Package Requirements */}
                    {pkg.requiredSpecializations.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs text-orange-600">
                          Requires specialization in: {pkg.requiredSpecializations.map(spec => getSpecializationLabel(spec)).join(', ')}
                        </p>
                      </div>
                    )}

                    {/* Price Input - Only show if package is selected */}
                    {isSelected && (
                      <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                        <Label htmlFor={`price-${pkg.id}`} className="text-sm font-medium text-green-800">
                          Your Price for {pkg.name} (€)
                        </Label>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-gray-500">€</span>
                          <Input 
                            id={`price-${pkg.id}`}
                            type="number"
                            min="10"
                            step="5"
                            value={currentPrice}
                            onChange={(e) => handlePackagePriceChange(pkg.id, Number(e.target.value))}
                            placeholder="Enter your price"
                            className="flex-1"
                          />
                        </div>
                        {errors.packagePricing?.[pkg.id] && (
                          <p className="text-red-600 text-xs mt-1">{errors.packagePricing[pkg.id]?.message}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {errors.offeredPackages && (
          <p className="text-red-600 text-sm mt-1">{errors.offeredPackages.message}</p>
        )}
      </div>

      {/* Terms Agreement */}
      <div className="flex items-start space-x-2 p-4 bg-green-50 rounded-lg border border-green-200">
        <Checkbox
          id="agreeToTerms"
          checked={watch("agreeToTerms")}
          onCheckedChange={(checked) => setValue("agreeToTerms", checked === true)}
        />
        <div className="flex-1">
          <Label htmlFor="agreeToTerms" className="text-sm font-medium text-green-800">
            ✅ I agree to the <a href="/expert-marketplace-terms" target="_blank" className="text-blue-600 underline hover:text-blue-800">Expert Marketplace Terms</a> and understand that all applications are manually reviewed. You'll hear back from us within 3–5 business days.
          </Label>
          <div className="mt-2 text-xs text-green-700">
            <p><strong>Key Terms:</strong> 20% commission on all bookings, secure payment processing, client matching, and professional support included.</p>
          </div>
        </div>
      </div>
      {errors.agreeToTerms && (
        <p className="text-red-600 text-sm">{errors.agreeToTerms.message}</p>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={onPrev}>
          Back
        </Button>
        
        <div className="flex gap-3">
          {onCheckStatus && (
            <Button 
              type="button" 
              variant="outline" 
              onClick={onCheckStatus}
              className="border-orange-200 text-orange-700 hover:bg-orange-50"
            >
              Check Application Status
            </Button>
          )}
          
          <Button 
            type="submit" 
            className="bg-grandpal-green hover:bg-grandpal-green/90"
            disabled={isSubmitting || isSending || isUploading || !isStepComplete}
          >
            {isSubmitting || isSending || isUploading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {isUploading ? "Uploading..." : isSubmitting ? "Submitting Application..." : "Sending Email..."}
              </>
            ) : (
              "Submit Application"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
